import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import UploadArea from './components/UploadArea';
import ImageCard from './components/ImageCard';
import EditImageModal from './components/EditImageModal';
import CropImageModal from './components/CropImageModal';
import BulkActionsBar from './components/BulkActionsBar';
import PortfolioStats from './components/PortfolioStats';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import Toast from '../../components/ui/Toast';

const PortfolioManagement = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [editingImage, setEditingImage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [filterCategory, setFilterCategory] = useState('all');
  const [photographerId, setPhotographerId] = useState(null);
  const [toast, setToast] = useState(null);
  // Crop queue: مصفوفة من { file, src } تنتظر الاقتصاص واحدة تلو الأخرى
  const [cropQueue, setCropQueue] = useState([]);
  const [cropCurrent, setCropCurrent] = useState(null);

  useEffect(() => {
    loadPortfolio();
  }, []);

  const loadPortfolio = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Auth error:', authError);
        throw authError;
      }
      
      if (!user) {
        console.error('No user found');
        throw new Error('Not authenticated');
      }

      console.log('Photographer ID loaded:', user.id);
      setPhotographerId(user.id);

      // Load portfolio images from database
      const { data: images, error } = await supabase
        .from('portfolio_images')
        .select('*')
        .eq('photographer_id', user.id)
        .order('created_at', { ascending: false });

      if (error && error.code !== '42P01') { // Table doesn't exist
        throw error;
      }

      if (images) {
        const formattedImages = images.map((img) => ({
          id: img.id,
          url: img.image_url,
          alt: img.description || img.title || 'صورة من المعرض',
          title: img.title || 'بدون عنوان',
          description: img.description || '',
          category: img.category || 'التصوير الشخصي',
          isFeatured: img.is_featured || false,
          views: img.views || 0,
          uploadDate: img.created_at ? img.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
          approvalStatus: img.approval_status || 'pending',
        }));
        setPortfolioImages(formattedImages);
      }
    } catch (error) {
      console.error('Failed to load portfolio:', error);
      setToast({ type: 'error', message: 'فشل تحميل المعرض' });
    } finally {
      setLoading(false);
    }
  };

  // عند اختيار ملفات جديدة: ابنِ queue وابدأ بأول صورة
  const handleFilesSelected = (files) => {
    const items = files.map((file) => ({
      file,
      src: URL.createObjectURL(file),
    }));
    // أول عنصر يُعرض فوراً، الباقي ينتظر
    setCropCurrent(items[0]);
    setCropQueue(items.slice(1));
  };

  // المصور أكّد الاقتصاص: استبدل الملف الأصلي بالمقصوص ثم انتقل للتالي
  const handleCropConfirm = (croppedFile) => {
    URL.revokeObjectURL(cropCurrent.src);
    processCropResult(croppedFile);
  };

  // المصور اختار "رفع بدون اقتصاص": استخدم الملف الأصلي
  const handleCropSkip = () => {
    URL.revokeObjectURL(cropCurrent.src);
    processCropResult(cropCurrent.file);
  };

  // إلغاء كل العملية (X)
  const handleCropCancel = () => {
    if (cropCurrent) URL.revokeObjectURL(cropCurrent.src);
    cropQueue.forEach((item) => URL.revokeObjectURL(item.src));
    setCropCurrent(null);
    setCropQueue([]);
  };

  // بعد اتخاذ القرار: ارفع الملف، ثم انتقل للصورة التالية في الـ queue
  const processCropResult = async (file) => {
    await handleUpload([file]);
    if (cropQueue.length > 0) {
      setCropCurrent(cropQueue[0]);
      setCropQueue((prev) => prev.slice(1));
    } else {
      setCropCurrent(null);
    }
  };

  const handleUpload = async (files) => {
    console.log('handleUpload called with files:', files);
    
    try {
      // Ensure photographerId is loaded
      let currentPhotographerId = photographerId;
      
      if (!currentPhotographerId) {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError || !user) {
          const errorMsg = 'يجب تسجيل الدخول أولاً';
          console.error(errorMsg);
          setToast({ type: 'error', message: errorMsg });
          return Promise.reject(new Error('Not authenticated'));
        }
        
        currentPhotographerId = user.id;
        console.log('Photographer ID loaded in handleUpload:', currentPhotographerId);
        setPhotographerId(currentPhotographerId);
      }

      if (!files || files.length === 0) {
        const errorMsg = 'لم يتم اختيار أي ملفات';
        console.error(errorMsg);
        setToast({ type: 'error', message: errorMsg });
        alert(errorMsg);
        return Promise.reject(new Error('No files provided'));
      }

      const MAX_PORTFOLIO_IMAGES = 8;
      const currentCount = portfolioImages.length;
      if (currentCount >= MAX_PORTFOLIO_IMAGES) {
        const errorMsg = `الحد الأقصى لصور المعرض ${MAX_PORTFOLIO_IMAGES} صور. لا يمكن رفع المزيد. احذف صورةً أولاً إن أردت إضافة غيرها.`;
        setToast({ type: 'error', message: errorMsg });
        alert(errorMsg);
        return Promise.reject(new Error(errorMsg));
      }
      const remainingSlots = MAX_PORTFOLIO_IMAGES - currentCount;
      if (files.length > remainingSlots) {
        const errorMsg = `الحد الأقصى ${MAX_PORTFOLIO_IMAGES} صور. لديك حالياً ${currentCount} صور. يمكنك رفع ${remainingSlots} صورة فقط.`;
        setToast({ type: 'error', message: errorMsg });
        alert(errorMsg);
        return Promise.reject(new Error(errorMsg));
      }

      console.log('Starting upload for', files.length, 'file(s)');
      setLoading(true);
      const newImages = [];
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`Processing file ${i + 1}/${files.length}:`, file.name);
        
        try {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            const errorMsg = `الملف ${file.name} ليس صورة. تم تخطيه.`;
            console.warn(errorMsg);
            setToast({ type: 'warning', message: errorMsg });
            errorCount++;
            continue;
          }

          // Validate file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            const errorMsg = `الملف ${file.name} كبير جداً (الحد الأقصى 10 ميجابايت). تم تخطيه.`;
            console.warn(errorMsg);
            setToast({ type: 'warning', message: errorMsg });
            errorCount++;
            continue;
          }

          // Create unique file name
          const fileExt = file.name.split('.').pop();
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substring(2, 9);
          const fileName = `${timestamp}-${randomStr}.${fileExt}`;
          // Store in photographer's folder: photographerId/filename
          const filePath = `${currentPhotographerId}/${fileName}`;

          console.log('Uploading file:', filePath, 'Size:', file.size);

          // Upload to Supabase Storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('portfolio-images')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Storage upload error:', uploadError);
            console.error('Error details:', {
              message: uploadError.message,
              statusCode: uploadError.statusCode,
              error: uploadError.error
            });

            // Show specific error message
            if (uploadError.message?.includes('new row violates row-level security')) {
              setToast({ 
                type: 'error', 
                message: 'خطأ في الصلاحيات. تأكد من إعدادات Storage Bucket' 
              });
            } else if (uploadError.message?.includes('Bucket not found')) {
              setToast({ 
                type: 'error', 
                message: 'Bucket غير موجود. تأكد من إنشاء portfolio-images bucket' 
              });
            } else {
              setToast({ 
                type: 'error', 
                message: `خطأ في الرفع: ${uploadError.message}` 
              });
            }

            // Fallback: Use base64 data URL as temporary solution
            console.log('Using fallback: base64 data URL');
            const reader = new FileReader();
            const imageUrl = await new Promise((resolve, reject) => {
              reader.onload = (e) => resolve(e.target.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            // Save to database with data URL (fallback only if storage upload fails)
            const { data: dbData, error: dbError } = await supabase
              .from('portfolio_images')
              .insert({
                photographer_id: currentPhotographerId,
                image_url: imageUrl,
                title: file.name.replace(/\.[^/.]+$/, ''),
                description: '',
                category: 'التصوير الشخصي',
                is_featured: false,
                views: 0,
                approval_status: 'pending',
              })
              .select()
              .single();

            if (dbError) {
              console.error('Failed to save image to database:', dbError);
              setToast({ 
                type: 'error', 
                message: `فشل حفظ الصورة في قاعدة البيانات: ${dbError.message}` 
              });
              continue;
            }

            newImages.push({
              id: dbData.id,
              url: dbData.image_url,
              alt: dbData.description || dbData.title || 'صورة جديدة',
              title: dbData.title || 'بدون عنوان',
              description: dbData.description || '',
              category: dbData.category || 'التصوير الشخصي',
              isFeatured: dbData.is_featured || false,
              views: dbData.views || 0,
              uploadDate: dbData.created_at ? dbData.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
              approvalStatus: dbData.approval_status || 'pending',
            });
            successCount++;
            console.log('Image saved with data URL (fallback):', file.name);
            continue;
          }

          console.log('File uploaded successfully:', uploadData);

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('portfolio-images')
            .getPublicUrl(filePath);

          const publicUrl = urlData?.publicUrl;
          
          if (!publicUrl) {
            console.error('Failed to get public URL');
            setToast({ type: 'error', message: 'فشل الحصول على رابط الصورة' });
            // Try to delete uploaded file
            await supabase.storage.from('portfolio-images').remove([filePath]);
            continue;
          }

          console.log('Public URL:', publicUrl);

          // Save to database (approval_status: pending until admin approves)
          const { data: dbData, error: dbError } = await supabase
            .from('portfolio_images')
            .insert({
              photographer_id: currentPhotographerId,
              image_url: publicUrl,
              title: file.name.replace(/\.[^/.]+$/, ''),
              description: '',
              category: 'التصوير الشخصي',
              is_featured: false,
              views: 0,
              approval_status: 'pending',
            })
            .select()
            .single();

          if (dbError) {
            console.error('Failed to save image to database:', dbError);
            setToast({ 
              type: 'error', 
              message: `فشل حفظ الصورة في قاعدة البيانات: ${dbError.message}` 
            });
            // Try to delete uploaded file if database insert fails
            try {
              await supabase.storage
                .from('portfolio-images')
                .remove([filePath]);
            } catch (removeError) {
              console.error('Failed to remove uploaded file:', removeError);
            }
            continue;
          }

          newImages.push({
            id: dbData.id,
            url: dbData.image_url,
            alt: dbData.description || dbData.title || 'صورة جديدة',
            title: dbData.title || 'بدون عنوان',
            description: dbData.description || '',
            category: dbData.category || 'التصوير الشخصي',
            isFeatured: dbData.is_featured || false,
            views: dbData.views || 0,
            uploadDate: dbData.created_at ? dbData.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
            approvalStatus: dbData.approval_status || 'pending',
          });
          successCount++;
          console.log('Image uploaded and saved successfully:', file.name);
        } catch (fileError) {
          errorCount++;
          const errorMsg = `خطأ في معالجة الملف ${file.name}: ${fileError.message || fileError}`;
          console.error(errorMsg, fileError);
          setToast({ type: 'error', message: errorMsg });
          alert(errorMsg); // Add alert as backup
        }
      }

      console.log(`Upload completed: ${successCount} succeeded, ${errorCount} failed`);

      if (newImages.length > 0) {
        setPortfolioImages([...newImages, ...portfolioImages]);
        const successMessage = newImages.length === files.length 
          ? `✅ تم رفع ${newImages.length} صورة بنجاح`
          : `✅ تم رفع ${newImages.length} من ${files.length} صورة بنجاح`;
        console.log(successMessage);
        setToast({ type: 'success', message: successMessage });
        alert(successMessage); // Add alert as backup
        return Promise.resolve();
      } else if (files.length > 0) {
        const errorMessage = '❌ فشل رفع جميع الصور. تحقق من:\n1) إعدادات Storage Bucket\n2) الصلاحيات (RLS Policies)\n3) حجم الملفات\n4) افتح Console لرؤية التفاصيل';
        console.error(errorMessage);
        setToast({ type: 'error', message: errorMessage });
        alert(errorMessage); // Add alert as backup
        return Promise.reject(new Error('No images uploaded'));
      }
      return Promise.resolve();
    } catch (error) {
      const errorMsg = `❌ فشل رفع الصور: ${error.message || error}`;
      console.error('Failed to upload images:', error);
      setToast({ type: 'error', message: errorMsg });
      alert(errorMsg); // Add alert as backup
      return Promise.reject(error);
    } finally {
      setLoading(false);
      console.log('Upload process finished');
    }
  };

  const handleSelectImage = (id) => {
    setSelectedImages((prev) =>
      prev.includes(id) ? prev.filter((imgId) => imgId !== id) : [...prev, id]
    );
  };

  const handleToggleFeatured = async (id) => {
    try {
      const image = portfolioImages.find((img) => img.id === id);
      if (!image || image.isFeatured) return; // already featured → do nothing
      if (image.approvalStatus !== 'approved') {
        setToast({ type: 'error', message: 'لا يمكن تعيين صورة غير منشورة كغلاف' });
        return;
      }

      // Unset all featured images for this photographer first
      await supabase
        .from('portfolio_images')
        .update({ is_featured: false })
        .eq('photographer_id', photographerId);

      // Set selected image as the sole cover
      const { error } = await supabase
        .from('portfolio_images')
        .update({ is_featured: true })
        .eq('id', id);

      if (error) throw error;

      setPortfolioImages((prev) =>
        prev.map((img) => ({ ...img, isFeatured: img.id === id }))
      );
      setToast({ type: 'success', message: 'تم تعيين صورة الغلاف بنجاح' });
    } catch (error) {
      console.error('Failed to set featured:', error);
      setToast({ type: 'error', message: 'فشل تعيين صورة الغلاف' });
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
  };

  const handleSaveEdit = async (updatedImage) => {
    try {
      const { error } = await supabase
        .from('portfolio_images')
        .update({
          title: updatedImage.title,
          description: updatedImage.description,
          category: updatedImage.category,
        })
        .eq('id', updatedImage.id);

      if (error) throw error;

      setPortfolioImages((prev) =>
        prev.map((img) => (img.id === updatedImage.id ? updatedImage : img))
      );
      setEditingImage(null);
      setToast({ type: 'success', message: 'تم حفظ التعديلات بنجاح' });
    } catch (error) {
      console.error('Failed to save edit:', error);
      setToast({ type: 'error', message: 'فشل حفظ التعديلات' });
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ ids: [id], count: 1 });
  };

  const handleBulkDelete = () => {
    setDeleteConfirm({ ids: selectedImages, count: selectedImages.length });
  };

  const confirmDelete = async () => {
    try {
      const { error } = await supabase
        .from('portfolio_images')
        .delete()
        .in('id', deleteConfirm.ids);

      if (error) throw error;

      setPortfolioImages((prev) =>
        prev.filter((img) => !deleteConfirm.ids.includes(img.id))
      );
      setSelectedImages([]);
      setDeleteConfirm(null);
      setToast({ type: 'success', message: 'تم حذف الصور بنجاح' });
    } catch (error) {
      console.error('Failed to delete images:', error);
      setToast({ type: 'error', message: 'فشل حذف الصور' });
    }
  };

  const handleBulkCategoryChange = async (category) => {
    if (!category) return;
    
    try {
      const { error } = await supabase
        .from('portfolio_images')
        .update({ category })
        .in('id', selectedImages);

      if (error) throw error;

      setPortfolioImages((prev) =>
        prev.map((img) =>
          selectedImages.includes(img.id) ? { ...img, category } : img
        )
      );
      setSelectedImages([]);
      setToast({ type: 'success', message: 'تم تحديث الفئات بنجاح' });
    } catch (error) {
      console.error('Failed to update categories:', error);
      setToast({ type: 'error', message: 'فشل تحديث الفئات' });
    }
  };

  const getFilteredAndSortedImages = () => {
    let filtered = portfolioImages;

    if (filterCategory !== 'all') {
      filtered = filtered.filter((img) => img.category === filterCategory);
    }

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'views':
          return b.views - a.views;
        case 'featured':
          return (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0);
        case 'recent':
        default:
          return new Date(b.uploadDate) - new Date(a.uploadDate);
      }
    });

    return sorted;
  };


  const stats = {
    totalImages: portfolioImages.length,
    featuredImages: portfolioImages.filter((img) => img.isFeatured).length,
    totalViews: portfolioImages.reduce((sum, img) => sum + img.views, 0),
    engagements: 0, // Can be calculated from interactions if needed
  };

  const sortOptions = [
    { value: 'recent', label: 'الأحدث' },
    { value: 'views', label: 'الأكثر مشاهدة' },
    { value: 'featured', label: 'المميزة أولاً' },
  ];

  const categoryOptions = [
    { value: 'all', label: 'جميع الفئات' },
    { value: 'wedding', label: 'تصوير حفلات الزفاف' },
    { value: 'portrait', label: 'التصوير الشخصي' },
    { value: 'event', label: 'تصوير المناسبات' },
    { value: 'real-estate', label: 'تصوير العقارات' },
    { value: 'food', label: 'تصوير الطعام' },
    { value: 'product', label: 'تصوير المنتجات' },
  ];

  const displayImages = getFilteredAndSortedImages();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-16 pb-20 lg:pb-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              إدارة المعرض
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              قم بإدارة وتنظيم صور معرضك الاحترافي
            </p>
          </div>

          <PortfolioStats stats={stats} />

          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3 md:px-5 md:py-4 mb-4 md:mb-6">
            <p className="text-xs md:text-sm leading-relaxed">
              نرجو رفع أعمالك الأصلية فقط. عرض صور ليست ملكك يؤدّي إلى إيقاف الحساب ومساءلة نظامية.
            </p>
          </div>

          <UploadArea
            onFilesSelected={handleFilesSelected}
            maxCount={8}
            currentCount={portfolioImages.length}
          />

          <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex-1">
                <Select
                  options={categoryOptions}
                  value={filterCategory}
                  onChange={setFilterCategory}
                  placeholder="تصفية حسب الفئة"
                />
              </div>

              <div className="flex-1">
                <Select
                  options={sortOptions}
                  value={sortBy}
                  onChange={setSortBy}
                  placeholder="ترتيب حسب"
                />
              </div>

{/* selection UI hidden */}
            </div>
          </div>

          {displayImages.length === 0 ? (
            <div className="bg-card rounded-xl p-8 md:p-12 lg:p-16 text-center shadow-warm">
              <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Image" size={32} className="md:w-10 md:h-10 lg:w-12 lg:h-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground mb-2">
                لا توجد صور في المعرض
              </h3>
              <p className="text-sm md:text-base text-muted-foreground">
                ابدأ برفع صورك الاحترافية لعرضها للعملاء
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {displayImages.map((image) => (
                <ImageCard
                  key={image.id}
                  image={image}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleFeatured={handleToggleFeatured}
                  isSelected={selectedImages.includes(image.id)}
                  onSelect={handleSelectImage}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {cropCurrent && (
        <CropImageModal
          imageSrc={cropCurrent.src}
          fileName={cropCurrent.file.name}
          onConfirm={handleCropConfirm}
          onSkip={handleCropSkip}
          onCancel={handleCropCancel}
        />
      )}

      {editingImage && (
        <EditImageModal
          image={editingImage}
          onClose={() => setEditingImage(null)}
          onSave={handleSaveEdit}
        />
      )}

      {deleteConfirm && (
        <DeleteConfirmModal
          count={deleteConfirm.count}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

{/* BulkActionsBar hidden */}

      {toast && (
        <Toast
          notification={{
            title: toast.type === 'success' ? 'نجح' : toast.type === 'error' ? 'خطأ' : 'تنبيه',
            message: toast.message,
            icon: toast.type === 'success' ? 'CheckCircle' : toast.type === 'error' ? 'AlertCircle' : 'Info',
            iconColor: toast.type === 'success' ? '#22c55e' : toast.type === 'error' ? '#ef4444' : '#f59e0b'
          }}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PortfolioManagement;
